import React, { useState, useEffect } from "react";
import {
    Button,
    Grid,
    Row,
    Col,
    Glyphicon,
    FormGroup,
    FormControl,
    Label,
    Table
} from "react-bootstrap";
import { View } from "react-native";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
    getTransfers,
    getCapById,
    getUserById,
    getBuilder,
    getAllBuilders
} from "../utils/bedrockAPI";
import "./Transfers.css";

const STATUS_STYLES = {
    EXPIRED: "default",
    COMPLETED: "success",
    REQUEST: "info",
    UPLOADED: "primary",
    INITIATED: "warning"
};

function getMonitorDisplay(monitorZmId, architecture) {
    if (monitorZmId == null || monitorZmId === "") return "—";
    const id = Number(monitorZmId);
    if (Number.isNaN(id)) return monitorZmId;
    const twoCam = architecture && (architecture.includes("2CAM") || architecture.endsWith("2CAM"));
    const fourCam = architecture && (architecture.includes("4CAM") || architecture.endsWith("4CAM"));
    if (twoCam) {
        if (id === 11) return 1;
        if (id === 12) return 2;
    }
    if (fourCam) {
        if (id === 5) return 1;
        if (id === 6) return 2;
        if (id === 7) return 3;
        if (id === 8) return 4;
    }
    return monitorZmId;
}

function formatDate(d) {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleString();
}

function datePartOnly(d) {
    if (!d) return "";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
}

function addDateDisplay(d) {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

/** Cap may have builderId or builderid (API); normalize for lookup and display. */
function getCapBuilderId(cap) {
    if (!cap) return null;
    const id = cap.builderId ?? cap.builderid;
    return id != null ? String(id) : null;
}

const VIDEO_URL_CUTOFF_DATE = "2026-03-01"; // Before this date: bedrock_video.mp4; on or after: <project>_<date>.mp4
const VIDEO_URL_FROM_API_CUTOFF = "2026-03-11"; // On or after this date: use transfer.url when present

function buildVideoUrl(transfer, cap, builder) {
    if (!transfer || transfer.status !== "COMPLETED" || !cap || !builder) return null;
    const date = datePartOnly(transfer.startDate);
    if (date && date > VIDEO_URL_FROM_API_CUTOFF && transfer.url) {
        return transfer.url;
    }
    const project = (transfer.project || "").replace(/\s+/g, "_");
    const builderSlug = transfer.builder;
    if (!project || !date || !builderSlug) return null;
    const filename = date < VIDEO_URL_CUTOFF_DATE ? "bedrock_video.mp4" : `${project}_${date}.mp4`;
    return `https://s3.amazonaws.com/bedrockwireless/share/security_events/${builderSlug}/${project}/${date}/${filename}`;
}

export default function Transfers({ history, builders: buildersProp }) {
    const [transfers, setTransfers] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [capsByCapId, setCapsByCapId] = useState({});
    const [usersByUserId, setUsersByUserId] = useState({});
    const [buildersByBuilderId, setBuildersByBuilderId] = useState({});

    const loadData = async (forceRefresh = false) => {
        if (!forceRefresh && (hasLoaded || loading)) return;
        setLoading(true);
        try {
            const list = await getTransfers();
            const rawTransfers = Array.isArray(list) ? list : [];
            setTransfers(rawTransfers);
            console.log("rawTransfers", rawTransfers);

            const capIds = [...new Set(rawTransfers.map((t) => t.capId).filter(Boolean))];
            const userIds = [...new Set(rawTransfers.map((t) => t.userId).filter(Boolean))];

            const caps = {};
            await Promise.all(
                capIds.map(async (capId) => {
                    try {
                        const cap = await getCapById(capId);
                        caps[capId] = cap;
                    } catch (e) {
                        caps[capId] = null;
                    }
                })
            );
            setCapsByCapId(caps);

            const users = {};
            await Promise.all(
                userIds.map(async (userId) => {
                    try {
                        const user = await getUserById(userId);
                        users[userId] = user;
                    } catch (e) {
                        users[userId] = null;
                    }
                })
            );
            setUsersByUserId(users);

            const buildersMap = {};
            const addBuilder = (b) => {
                if (!b) return;
                const id = b.builderId ?? b.builder_id ?? b.id;
                if (id != null) buildersMap[String(id)] = b;
            };
            if (buildersProp && buildersProp.length > 0) {
                buildersProp.forEach(addBuilder);
            } else {
                try {
                    const allBuilders = await getAllBuilders();
                    (Array.isArray(allBuilders) ? allBuilders : []).forEach(addBuilder);
                } catch (e) {}
            }
            const builderIds = [...new Set(Object.values(caps).filter(Boolean).map((c) => getCapBuilderId(c)).filter(Boolean))];
            const missingBuilderIds = builderIds.filter((id) => !buildersMap[id]);
            await Promise.all(
                missingBuilderIds.map(async (builderId) => {
                    try {
                        const builder = await getBuilder(builderId);
                        buildersMap[builderId] = builder;
                    } catch (e) {
                        buildersMap[builderId] = null;
                    }
                })
            );
            setBuildersByBuilderId(buildersMap);
        } catch (error) {
            console.error("Error loading transfers:", error);
            setTransfers([]);
        }
        setHasLoaded(true);
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const toggleExpanded = (transferId) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(transferId)) next.delete(transferId);
            else next.add(transferId);
            return next;
        });
    };

    const filteredTransfers = (transfers || [])
        .filter((t) => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            const cap = capsByCapId[t.capId];
            const capName = cap && (cap.neighborhood ? cap.neighborhood + ", " + cap.name : cap.name);
            const user = usersByUserId[t.userId];
            const email = user && user.email;
            const project = (t.project || "").toLowerCase();
            const id = (t.transferId || "").toString().toLowerCase();
            return (
                (capName && capName.toLowerCase().includes(term)) ||
                (email && email.toLowerCase().includes(term)) ||
                project.includes(term) ||
                id.includes(term)
            );
        })
        .sort((a, b) => {
            const dateA = a.addDate ? new Date(a.addDate).getTime() : 0;
            const dateB = b.addDate ? new Date(b.addDate).getTime() : 0;
            return dateB - dateA;
        });

    return (
        <View className="transfers-container">
            <div className="transfers-search">
                <FormGroup>
                    <div className="transfers-search-row">
                        <FormControl
                            type="text"
                            placeholder="Search by transfer ID, cap name, email, or project..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="transfers-search-input"
                        />
                        <Button
                            bsStyle="default"
                            onClick={() => setSearchTerm("")}
                            className="transfers-clear-button"
                            title="Clear search"
                        >
                            <Glyphicon glyph="remove" />
                        </Button>
                        <Button
                            bsStyle="default"
                            onClick={() => loadData(true)}
                            disabled={loading}
                            className="transfers-refresh-button"
                            title="Refresh transfer data"
                        >
                            <Glyphicon glyph="refresh" className={loading ? "glyphicon-spin" : ""} />
                        </Button>
                    </div>
                </FormGroup>
            </div>
            {hasLoaded && (
                <div className="transfers-count">
                    Transfers: {filteredTransfers.length} of {transfers ? transfers.length : 0}
                </div>
            )}
            <Grid fluid>
                <Row>
                    <Col md={12}>
                        {loading ? (
                            <div className="transfers-loading">
                                <Glyphicon glyph="refresh" className="glyphicon-spin" style={{ fontSize: "24px", marginRight: "8px" }} />
                                Loading transfers...
                            </div>
                        ) : !transfers ? (
                            <div className="transfers-empty">No data yet.</div>
                        ) : filteredTransfers.length === 0 ? (
                            <div className="transfers-empty">No transfers match the search.</div>
                        ) : (
                            <div className="transfers-table-wrapper">
                                <Table responsive striped bordered hover className="transfers-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Transfer Name</th>
                                            <th className="transfers-cap-cell">Camera</th>
                                            <th>User</th>
                                            <th>Status</th>
                                            <th>Builder</th>
                                            <th>Video</th>
                                            <th className="transfers-th-expand"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTransfers.map((t, idx) => {
                                            const cap = capsByCapId[t.capId];
                                            const user = usersByUserId[t.userId];
                                            const capBuilderId = getCapBuilderId(cap);
                                            const builder = capBuilderId ? buildersByBuilderId[capBuilderId] : null;
                                            const capName = cap && (cap.neighborhood ? cap.neighborhood + ", " + cap.name : cap.name);
                                            const monitorNum = getMonitorDisplay(t.monitorZmId, cap.architecture);
                                            const partsCount = Array.isArray(t.events) ? t.events.length : 0;
                                            const videoUrl = buildVideoUrl(t, cap, builder);
                                            const isExpanded = expandedIds.has(t.transferId);

                                            return (
                                                <React.Fragment key={t.transferId || idx}>
                                                    <tr>
                                                        <td className="transfers-date-cell">{addDateDisplay(t.addDate)}</td>
                                                        <td className="transfers-project-cell">{t.project ?? "—"}</td>
                                                        <td className="transfers-cap-cell">
                                                            {t.capId ? (
                                                                <a
                                                                    href={`/caps/${t.capId}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="transfers-link"
                                                                >
                                                                    {capName != null ? capName : t.capId}
                                                                </a>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </td>
                                                        <td className="transfers-user-cell">
                                                            {t.userId ? (
                                                                capBuilderId ? (
                                                                    <a
                                                                        href={`/accesscontrol/${capBuilderId}/user/${t.userId}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="transfers-link"
                                                                    >
                                                                        {user && user.email ? user.email : t.userId}
                                                                    </a>
                                                                ) : (
                                                                    <span>{user && user.email ? user.email : t.userId}</span>
                                                                )
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Label bsStyle={STATUS_STYLES[t.status] || "default"}>
                                                                {t.status || "—"}
                                                            </Label>
                                                        </td>
                                                        <td>
                                                            {capBuilderId ? (
                                                                <a
                                                                    href={`/accesscontrol/${capBuilderId}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="transfers-link"
                                                                >
                                                                    {builder && builder.name != null ? builder.name : capBuilderId}
                                                                </a>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </td>
                                                        <td className="transfers-video-cell">
                                                            {videoUrl ? (
                                                                <Button
                                                                    bsSize="small"
                                                                    bsStyle="primary"
                                                                    href={videoUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="transfers-play-btn"
                                                                >
                                                                    <Glyphicon glyph="play" /> Play
                                                                </Button>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </td>
                                                        <td className="transfers-expand-cell">
                                                            <Button
                                                                bsSize="xs"
                                                                className="transfers-expand-btn"
                                                                onClick={() => toggleExpanded(t.transferId)}
                                                                title={isExpanded ? "Collapse" : "Expand details"}
                                                            >
                                                                <Glyphicon glyph={isExpanded ? "chevron-up" : "chevron-down"} />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr className="transfers-expanded-row">
                                                            <td colSpan="8" className="transfers-expanded-content">
                                                                <Row>
                                                                    <Col md={6}>
                                                                        <div className="transfers-expanded-item">
                                                                            <strong>Transfer ID:</strong>{" "}
                                                                            <span className="transfers-expanded-monospace">{t.transferId ?? "—"}</span>{" "}
                                                                            <CopyToClipboard text={String(t.transferId || "")}>
                                                                                <Button bsSize="xs" title="Copy transfer ID">
                                                                                    <Glyphicon glyph="copy" />
                                                                                </Button>
                                                                            </CopyToClipboard>
                                                                        </div>
                                                                        <div className="transfers-expanded-item">
                                                                            <strong>View:</strong> {monitorNum}
                                                                        </div>
                                                                        <div className="transfers-expanded-item">
                                                                            <strong>Parts:</strong> {partsCount}
                                                                        </div>
                                                                    </Col>
                                                                    <Col md={6}>
                                                                        <div className="transfers-expanded-item">
                                                                            <strong>Start date:</strong> {formatDate(t.startDate)}
                                                                        </div>
                                                                        <div className="transfers-expanded-item">
                                                                            <strong>End date:</strong> {formatDate(t.endDate)}
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Col>
                </Row>
            </Grid>
        </View>
    );
}
